import { useCallback, useEffect, useState } from "react"
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom"
import { Alert, Box, Button, CircularProgress, Link, Stack, Typography } from "@mui/material"
import { authApi } from "../api"
import { useAuth } from "../context/AuthContext"
import AuthLayout from "./AuthLayout"
import PasswordFields, { passwordAccepted } from "./PasswordFields"

/*
 * One screen for two entry points, because the server has one endpoint for both:
 * an invite and a reset do the same three things — check the token, write the
 * hash, revoke every session. Only the wording differs, so only the wording is
 * branched.
 */
const COPY = {
  invite: {
    title: "Set your password",
    subtitle: "Financials platform",
    intro: "Choose a password to finish setting up your account.",
    submit: "Set password",
    busy: "Setting password…",
    done: "Your password is set.",
  },
  reset: {
    title: "Choose a new password",
    subtitle: "Financials platform",
    intro: "Pick a new password for your account.",
    submit: "Save password",
    busy: "Saving…",
    done: "Your password has been changed.",
  },
}

export default function SetPassword({ mode = "reset" }) {
  const copy = COPY[mode] || COPY.reset
  const [params] = useSearchParams()
  const token = params.get("token") || ""
  const navigate = useNavigate()
  const { isAuthenticated, logout } = useAuth()

  const [checking, setChecking] = useState(true)
  const [tokenState, setTokenState] = useState(null)
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  // One question, asked once: the meter, the checklist and the button all read
  // the same answer, so the button can never be live on a password the checklist
  // is still marking incomplete.
  const canSubmit = passwordAccepted(password) && password === confirm

  /*
   * Check the link before asking for a password. An expired invite that only
   * fails after somebody has chosen and typed a password twice is the version of
   * this screen people write complaints about.
   */
  useEffect(() => {
    let cancelled = false
    if (!token) {
      setChecking(false)
      setTokenState({ valid: false, message: "This link is missing its token. Open the link from your email again." })
      return undefined
    }

    authApi
      .checkResetToken(token)
      .then((data) => {
        if (!cancelled) setTokenState(data)
      })
      .catch((err) => {
        if (!cancelled) {
          setTokenState({
            valid: false,
            message: err.response?.data?.message || err.userMessage || "That link is no longer valid.",
          })
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })

    return () => {
      cancelled = true
    }
  }, [token])

  const submit = useCallback(
    async (event) => {
      event.preventDefault()
      setError(null)
      /* No field-level faults here any more. The fields say what is wrong where
         it is wrong, and the button is disabled until nothing is — so a red
         banner at the top of the card would be a third statement of a fault
         already stated twice. The alert is now only for things the fields could
         not have known: an expired link, a token already spent, a server that
         refused. */
      if (!canSubmit) return

      setBusy(true)
      try {
        await authApi.resetPassword(token, password)
        // The server revoked every session for this user, this tab's included.
        // Clearing locally keeps the app from discovering that on its next
        // request and showing a "your session ended" banner over the success.
        if (isAuthenticated) await logout()
        setDone(true)
      } catch (err) {
        if (err.response?.data?.code === "RESET_TOKEN_INVALID") {
          // Expired between the check and the submit. Send them back to the
          // dead-link state rather than leaving a filled-in form they cannot
          // submit.
          setTokenState({ valid: false, message: err.response.data.message })
          return
        }
        setError(err.userMessage || "Could not set your password")
      } finally {
        setBusy(false)
      }
    },
    [canSubmit, isAuthenticated, logout, password, token],
  )

  if (checking) {
    return (
      <AuthLayout subtitle={copy.subtitle}>
        <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    )
  }

  if (tokenState && !tokenState.valid) {
    return (
      <AuthLayout title="This link has expired" subtitle={copy.subtitle}>
        <Stack spacing={2}>
          <Alert severity="warning">{tokenState.message || "That link is no longer valid."}</Alert>
          <Typography variant="body2" color="text.secondary">
            Reset links are single use and time limited. Request a fresh one and it will arrive in the same inbox.
          </Typography>
          <Button component={RouterLink} to="/forgot-password" variant="contained" size="large" fullWidth>
            Request a new link
          </Button>
          <Link component={RouterLink} to="/login" variant="body2" underline="hover" align="center">
            Back to sign in
          </Link>
        </Stack>
      </AuthLayout>
    )
  }

  if (done) {
    return (
      <AuthLayout title={copy.done} subtitle={copy.subtitle}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Everywhere you were signed in has been signed out, so sign in again with your new password.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate("/login", { replace: true })} fullWidth>
            Sign in
          </Button>
        </Stack>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={copy.title} subtitle={copy.subtitle}>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <form onSubmit={submit}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {tokenState?.username ? `Signing in as ${tokenState.username}. ` : ""}
            {copy.intro}
          </Typography>

          {/* A hidden username field is what lets a password manager file the new
              credential against the right account rather than prompting to create
              an orphan entry. */}
          <input type="hidden" name="username" autoComplete="username" value={tokenState?.username || ""} readOnly />

          <PasswordFields
            password={password}
            confirm={confirm}
            onPasswordChange={setPassword}
            onConfirmChange={setConfirm}
            disabled={busy}
            autoFocus
          />

          <Button type="submit" variant="contained" size="large" disabled={busy || !canSubmit} fullWidth>
            {busy ? copy.busy : copy.submit}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  )
}
